/* eslint-disable max-len */
import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { type GiftVoucherModuleSpecsFragment } from '../../../../api/fragments';
import {
    UpdateGiftVoucherModuleEmailContentDocument,
    type UpdateGiftVoucherModuleEmailContentMutation,
    type UpdateGiftVoucherModuleEmailContentMutationVariables,
} from '../../../../api/mutations/updateGiftVoucherModuleEmailContent';
import { EmailContentUpdateType } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import GiftVoucherModuleEmailForm from './GiftVoucherModuleEmailForm';
import { type GiftVoucherEmailFormValues } from './shared';
import useHandleGiftVoucherModuleAssets from './useHandleGiftVoucherModuleAssets';

const validatorEmailContentItem = validators.compose(
    validators.requiredString('subject.defaultValue.defaultValue'),
    validators.requiredString('introTitle.defaultValue.defaultValue'),
    validators.requiredString('contentText.defaultValue.defaultValue')
);

const validator = validators.compose(
    validators.nest('customer', validators.compose(validators.nest('confirmation', validatorEmailContentItem)))
);

type GiftVoucherModuleEmailProps = {
    module: GiftVoucherModuleSpecsFragment;
};

const GiftVoucherModuleEmail = ({ module }: GiftVoucherModuleEmailProps) => {
    const { t } = useTranslation(['giftVoucherModuleDetails']);
    const apolloClient = useApolloClient();
    const validate = useValidator(validator);

    const handleGiftVoucherModuleAssets = useHandleGiftVoucherModuleAssets();

    const onSubmit = useHandleError(
        async ({ customer }: GiftVoucherEmailFormValues) => {
            message.loading({
                content: t('giftVoucherModuleDetails:messages.updateEmailContentsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient.mutate<
                UpdateGiftVoucherModuleEmailContentMutation,
                UpdateGiftVoucherModuleEmailContentMutationVariables
            >({
                mutation: UpdateGiftVoucherModuleEmailContentDocument,
                variables: {
                    moduleId: module.id,
                    settings: {
                        emailContentUpdateType: EmailContentUpdateType.Module,
                        customer: {
                            confirmation: omit(['introImage'], customer.confirmation),
                        },
                    },
                },
            });

            await handleGiftVoucherModuleAssets(module.id, { customer }, module.emailContents);

            await message.success({
                content: t('giftVoucherModuleDetails:messages.updateEmailContentsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, handleGiftVoucherModuleAssets, module.emailContents, module.id, t]
    );

    return (
        <Formik initialValues={module.emailContents} onSubmit={onSubmit} validate={validate} enableReinitialize>
            {({ handleSubmit }) => (
                <Form id="updateEmailContent" name="updateEmailContent" onSubmitCapture={handleSubmit}>
                    <GiftVoucherModuleEmailForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default GiftVoucherModuleEmail;
